import React, { Component } from 'react';
import { Card, Form, Badge, Button, ButtonGroup, ToggleButton, Row, Col, Modal, Table, Alert } from 'react-bootstrap';
import { GoX, GoPlus, GoArrowUp } from "react-icons/go";
import { GrDocument } from "react-icons/gr";
import { LuTableProperties, LuComponent } from "react-icons/lu";
import { CgDetailsMore } from "react-icons/cg";
import Tooltip from "../../controls/common/tooltip";
import Editor from "components/controls/editor";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/mode-yaml";
import YAML from "yaml";
import Utils from "context/utils";

// Modal used to edit value-typed session-object envelopes:
//   - { type: "message", message: { role, content, attributes, providers, metadata } }
//   - { type: "thread",  thread:  { attributes, metadata } }
//   - { type: "graph",   graph:   { attributes, metadata } }
//
// The "envelope-only" variants (thread, graph) expose just attributes +
// metadata — content/role/providers don't apply. Everything else (root header,
// JSON field editor, validation, save) is shared.
//
// The editor honors three special keys in the envelope's `attributes` bag:
//   - syntax_mode    — Ace highlighting for the content editor (text|markdown|json|yaml|python)
//   - object_format  — how attributes/providers/metadata render + edit (json|yaml)  [objectFormatOf]
//   - default_view   — which tab opens first, if valid for this object's tab set   [defaultViewOf]
//
// Editability: every editing affordance is gated on canEdit(), which is the revision's editability
// AND the absence of an explicit `readOnly` prop. Callers that can edit the revision but still must
// not write pass readOnly — the playground's session-object viewer does exactly that when the
// mutation gate is closed (an editable revision whose session is closed), where an Apply button
// would offer a write the server rejects.
export default class EditMessageContentModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      portValue: null,
      viewOption: "content",
      fields: {},
      errors: {}
    };

    this.messageViewOptions = [
      { label: <GrDocument/>, value: "content", tooltip: "Content" },
      { label: <LuTableProperties/>, value: "attributes", tooltip: "Attributes" },
      { label: <LuComponent/>, value: "providers", tooltip: "Providers" },
      { label: <CgDetailsMore/>, value: "metadata", tooltip: "Metadata" },
    ];

    this.threadViewOptions = [
      { label: <LuTableProperties/>, value: "attributes", tooltip: "Attributes" },
      { label: <CgDetailsMore/>, value: "metadata", tooltip: "Metadata" },
    ];
  }

  // Whether the modal's editing affordances render at all. The revision must be editable AND the
  // caller must not have forced view-only via the readOnly prop (see the class comment).
  canEdit() {
    return !this.props.readOnly && this.props.shell.shellInterface.canEditRevision();
  }

  // The key under which the envelope's payload lives — "message", "thread", or
  // "graph". Unknown/absent types fall back to "message".
  innerKeyFor(value) {
    if ( value && (value.type === "thread" || value.type === "graph") ) {
      return value.type;
    }
    return "message";
  }

  getInnerKey() {
    return this.innerKeyFor(this.state.portValue);
  }

  // Envelope-only value types (thread, graph) carry just attributes + metadata —
  // no message content / role / providers.
  isEnvelopeOnly() {
    const key = this.getInnerKey();
    return key === "thread" || key === "graph";
  }

  // Capitalized label for the active type: "Message" / "Thread" / "Graph".
  typeLabel() {
    const key = this.getInnerKey();
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  getViewOptions() {
    return this.isEnvelopeOnly() ? this.threadViewOptions : this.messageViewOptions;
  }

  // Initial JSON-field set: envelope-only types (thread, graph) have just
  // attributes + metadata; message has those plus providers.
  fieldKeysFor(value) {
    const key = this.innerKeyFor(value);
    return ( key === "thread" || key === "graph" )
      ? ["attributes", "metadata"]
      : ["attributes", "providers", "metadata"];
  }

  createFieldValue(field) {
    const value = this.props.portValue;
    const key = this.innerKeyFor(value);
    return this.serializeField(value[key][field], this.objectFormatOf(value));
  }

  // The object-editor format for the attributes / providers / metadata fields.
  // Stored in the envelope's own "attributes" bag under "object_format": "yaml"
  // renders and edits those fields as YAML, anything else (the default) as JSON.
  objectFormatOf(value) {
    const key = this.innerKeyFor(value);
    const attributes = value && value[key] && value[key].attributes;
    return attributes && attributes.object_format === "yaml" ? "yaml" : "json";
  }

  getObjectFormat() {
    return this.objectFormatOf(this.state.portValue);
  }

  // The editor tab to open first. Stored in the envelope's own "attributes" bag under "default_view";
  // honored only when it names a tab VALID for this object (message: content/attributes/providers/metadata;
  // thread/graph: attributes/metadata), otherwise falls back to the type's default tab.
  defaultViewOf(value) {
    const key = this.innerKeyFor(value);
    const isEnvelopeOnly = key === "thread" || key === "graph";
    const allowed = (isEnvelopeOnly ? this.threadViewOptions : this.messageViewOptions).map(o => o.value);
    const attributes = value && value[key] && value[key].attributes;
    const requested = attributes && attributes.default_view;
    if ( requested && allowed.includes(requested) ) {
      return requested;
    }
    return isEnvelopeOnly ? "attributes" : "content";
  }

  // Serialize a field object to editor text in the given format ("" when absent).
  serializeField(object, format) {
    if ( object === undefined || object === null ) {
      return "";
    }
    return format === "yaml" ? YAML.stringify(object) : JSON.stringify(object, null, 2);
  }

  // Parse editor text back into a value using the given format. Mirrors the JSON
  // path's "empty input is invalid" behaviour for YAML (an empty document would
  // otherwise parse to null).
  parseField(text, format) {
    if ( format === "yaml" ) {
      if ( text.trim() === "" ) {
        throw new Error("empty YAML document");
      }
      return YAML.parse(text);
    }
    return JSON.parse(text);
  }

  setError(error, message) {
    this.state.errors[error] = message;
    this.setState({errors: this.state.errors});
  }

  clearError(error) {
    delete this.state.errors[error];
    this.setState({errors: this.state.errors});
  }

  // A message value may carry only metadata (no content) — e.g. a config object
  // stored under message.metadata. The content editor maps/splices over content,
  // so ensure a content array exists (metadata and the rest of the value are kept).
  ensureMessageContent(portValue) {
    if ( this.innerKeyFor(portValue) === "message" ) {
      if ( !portValue.message ) {
        portValue.message = {};
      }
      if ( !Array.isArray(portValue.message.content) ) {
        portValue.message.content = [];
      }
    }
    return portValue;
  }

  onEnter = () => {
    const portValue = this.ensureMessageContent(Utils.deepCopy(this.props.portValue));
    const fieldKeys = this.fieldKeysFor(this.props.portValue);
    const fields = {};
    for ( const k of fieldKeys ) {
      fields[k] = this.createFieldValue(k);
    }
    this.setState({
      portValue: portValue,
      fields: fields,
      errors: {},
      // Initial tab: honor attributes.default_view when it names a tab valid for this object, else the
      // type default (content for message; attributes for envelope-only thread/graph). See defaultViewOf.
      viewOption: this.defaultViewOf(this.props.portValue)
    });
  }

  handleUpdate = () => {
    // Check if custom save handler is provided
    if ( this.props.onSave ) {
      this.props.onSave(this.state.portValue);
      this.props.onHide();
      return;
    }

    // Update the envelope payload (message, thread, or graph) on the original
    // portValue so the parent component picks up the changes.
    const key = this.innerKeyFor(this.props.portValue);
    this.props.portValue[key] = this.state.portValue[key];
    this.props.shell.shellInterface.markModified();

    // Close modal
    this.props.onHide();
  }

  /**
   * Root Properties
   */

  renderRootProperties() {
    const key = this.getInnerKey();
    const payload = this.state.portValue[key] || {};
    const isEnvelopeOnly = this.isEnvelopeOnly();

    let createdAt = "n/a";
    if ( payload["attributes"] && payload["attributes"]["created_at"] ) {
      createdAt = Utils.formatDateTime(payload["attributes"]["created_at"]);
    }

    return (
      <Row style={{"margin-bottom": "12px"}}>
        <Col sm={"auto"} className="d-flex align-items-center">
          <span className="text-muted" style={{"font-size": "14px"}}>
            Type
          </span>
          &nbsp;&nbsp;
          <Badge variant="secondary" className="title-tag-badge">
            {key}
          </Badge>
          {!isEnvelopeOnly &&
            <>
              &nbsp;&nbsp;
              <span className="text-muted" style={{"font-size": "14px"}}>
                Role
              </span>
              &nbsp;&nbsp;
              <Badge variant="secondary" className="title-tag-badge">
                {payload["role"]}
              </Badge>
            </>
          }
          &nbsp;&nbsp;
          <span className="text-muted" style={{"font-size": "14px"}}>
            Time
          </span>
          &nbsp;&nbsp;
          <Badge variant="secondary" className="title-tag-badge">
            {createdAt}
          </Badge>
        </Col>
        <Col>
          <span className="float-right">
            <ButtonGroup toggle size="sm">
              {this.getViewOptions().map((option, index) => (
                <Tooltip text={option.tooltip}>
                  <ToggleButton
                    key={index}
                    type="radio"
                    variant="light"
                    name="radio"
                    value={option.value}
                    checked={this.state.viewOption === option.value}
                    onChange={(event) => { this.setState({ viewOption: event.currentTarget.value }) }}
                  >
                    {option.label}
                  </ToggleButton>
                </Tooltip>
              ))}
            </ButtonGroup>
          </span>
        </Col>
      </Row>
    );
  }

  /**
   * Content Editor
   */

  handleInsertContentItem(index) {
    // Content editor is message-only.
    this.state.portValue.message.content.splice(index, 0, {
      "type": "text",
      "text": ""
    });
    this.setState({ portValue: this.state.portValue });
  }

  handleAddTextContentItem() {
    this.handleInsertContentItem(this.state.portValue.message.content.length);
  }

  handleRemoveContentItem(index) {
    this.state.portValue.message.content.splice(index, 1);
    this.setState({ portValue: this.state.portValue });
  }

  renderContent() {
    let mode = "text";
    if ( this.state.portValue.message.attributes && this.state.portValue.message.attributes["syntax_mode"] ) {
      mode = this.state.portValue.message.attributes["syntax_mode"];
    }
    if ( new Set(["text", "markdown", "json", "yaml", "python"]).has(mode) === false ) {
      mode = 'text';
    }

    return (
      <>
        <Table hover bordered>
          {((this.state.portValue.message && this.state.portValue.message.content) || []).map((contentItem, index) => {
            return (
              <tr>
                <td>
                  <div>
                    <Form.Row className="align-items-center" style={{ "margin-bottom": "10px" }}>
                      <Col xs="auto">
                        <span className="text-muted" style={{"font-size": "12px"}}>
                          Type
                        </span>
                      </Col>
                      <Col xs="auto">
                        <Badge variant="secondary" className="title-tag-badge">
                          text
                        </Badge>
                      </Col>
                      <Col>
                        <span className="float-right">
                          {this.canEdit() &&
                            <>
                              <Tooltip text={"Insert new content item of \"text\" type above this one"}>
                                <Button variant="light" size="sm" onClick={() => this.handleInsertContentItem(index)}>
                                  <GoPlus /><GoArrowUp />
                                </Button>
                              </Tooltip>
                              &nbsp;&nbsp;
                              <Tooltip text="Remove content item">
                                <Button variant="light" size="sm" onClick={() => this.handleRemoveContentItem(index)}>
                                  <GoX />
                                </Button>
                              </Tooltip>
                            </>
                          }
                        </span>
                      </Col>
                    </Form.Row>
                    <Form.Row className="align-items-center">
                      <Col>
                        <Editor
                          name="message-content-item-editor"
                          width="100%"
                          mode={mode}
                          wrapEnabled="true"
                          readOnly={!this.canEdit()}
                          value={contentItem.text}
                          minLines={3}
                          maxLines={55}
                          setOptions={{
                            "printMarginColumn": 120
                          }}
                          onChange={(newValue) => {
                            contentItem.text = newValue;
                          }}
                        />
                      </Col>
                    </Form.Row>
                  </div>
                </td>
              </tr>
            );
          })}
        </Table>

        {this.canEdit() &&
          <>
            <Button variant="success" size="sm" onClick={() => this.handleAddTextContentItem()}>
              Add Text
            </Button>
            <span className="text-muted text-note">
              &nbsp;&nbsp;&nbsp;Add new content item of "text" type
            </span>
          </>
        }
      </>
    );
  }

  /**
   * JSON fields (attributes, providers, metadata) — shared by message and thread.
   */

  handleAddField(field) {
    const key = this.getInnerKey();
    this.state.portValue[key][field] = {};
    this.state.fields[field] = this.serializeField({}, this.getObjectFormat());
    this.setState({
      portValue: this.state.portValue,
      fields: this.state.fields
    });
  }

  handleUpdateField(field, newValue) {
    // Update text value
    this.state.fields[field] = newValue;
    this.setState({fields: this.state.fields});

    // Update serialized object
    const format = this.getObjectFormat();
    try {
      const key = this.getInnerKey();
      this.state.portValue[key][field] = this.parseField(newValue, format);
      this.setState({portValue: this.state.portValue});
      this.clearError(`${field}_invalid`);
    } catch (error) {
      this.setError(`${field}_invalid`, `"${field}" field contains invalid ${format.toUpperCase()}`);
    }
  }

  handleRemoveField(field) {
    const key = this.getInnerKey();
    delete this.state.portValue[key][field];
    delete this.state.fields[field];
    this.setState({
      portValue: this.state.portValue,
      fields: this.state.fields
    });
  }

  renderField(field) {
    const key = this.getInnerKey();
    const payload = this.state.portValue[key];
    const noun = this.typeLabel();
    if ( !payload[field] ) {
      return (
        <>
          <Row>
            {/* The zero right padding exists to close the gap between the alert and the Add button
                beside it. With no button (view-only), it would leave the alert flush against the
                row's right edge, so the gutter comes back. */}
            <Col style={this.canEdit() ? {"paddingRight": "0px"} : undefined}>
              <Alert variant="info">
                {noun} "{field}" field is not specified.
              </Alert>
            </Col>
            {this.canEdit() &&
              <Col sm="auto">
                <Button variant="success" size="sm" onClick={() => this.handleAddField(field)}>
                  Add
                </Button>
              </Col>
            }
          </Row>
        </>
      );
    }
    return (
      <>
        <Form.Row className="align-items-center" style={{ "margin-bottom": "10px" }}>
          <Col xs="auto">
            <span className="text-muted" style={{"font-size": "12px"}}>
              Field
            </span>
          </Col>
          <Col xs="auto">
            <Badge variant="secondary" className="title-tag-badge">
              {field}
            </Badge>
          </Col>
          <Col>
            <span className="float-right">
              {this.canEdit() &&
                <Tooltip text={`Remove \"${field}\" field`}>
                  <Button variant="light" size="sm" onClick={() => this.handleRemoveField(field)}>
                    <GoX />
                  </Button>
                </Tooltip>
              }
            </span>
          </Col>
        </Form.Row>

        <Card.Text>
          <Editor
            name={`playground-widget-${noun.toLowerCase()}-${field}`}
            width="100%"
            mode={this.getObjectFormat()}
            wrapEnabled="true"
            readOnly={!this.canEdit()}
            value={this.state.fields[field]}
            onChange={(newValue) => this.handleUpdateField(field, newValue)}
            minLines={3}
            maxLines={30}
          />
        </Card.Text>
      </>
    );
  }

  /**
   * Main Renderer
   */

  render() {
    const hasError = Object.keys(this.state.errors).length > 0;

    const isEnvelopeOnly = this.isEnvelopeOnly();
    const title = "Edit " + this.typeLabel();

    return (
      <Modal
        {...this.props}
        onEnter={this.onEnter}
        size="xl"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.state.portValue &&
            <>
              {this.renderRootProperties()}

              {!isEnvelopeOnly &&
                <div className={"content" === this.state.viewOption ? "" : "hidden"}>
                  {this.renderContent()}
                </div>
              }
              <div className={"attributes" === this.state.viewOption ? "" : "hidden"}>
                <Table bordered hover>
                  <tbody><tr><td>
                    {this.renderField("attributes")}
                  </td></tr></tbody>
                </Table>
              </div>
              {!isEnvelopeOnly &&
                <div className={"providers" === this.state.viewOption ? "" : "hidden"}>
                  <Table bordered hover>
                    <tbody><tr><td>
                      {this.renderField("providers")}
                    </td></tr></tbody>
                  </Table>
                </div>
              }
              <div className={"metadata" === this.state.viewOption ? "" : "hidden"}>
                <Table bordered hover>
                  <tbody><tr><td>
                    {this.renderField("metadata")}
                  </td></tr></tbody>
                </Table>
              </div>
            </>
          }
        </Modal.Body>
        <Modal.Footer>
          {hasError &&
            <span style={{"padding-right": "10px"}}>
              <Alert variant="warning">
                {this.state.errors[Object.keys(this.state.errors)[0]]}
              </Alert>
            </span>
          }

          <Button variant="light" onClick={this.props.onHide}>Close</Button>
          {this.canEdit() &&
            <Button variant="success" disabled={hasError} onClick={this.handleUpdate}>Apply</Button>
          }
        </Modal.Footer>
      </Modal>
    );
  }
}
